import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { LabelValueSource, LabelValueAcceptedBy } from 'graphql/types';

import { useCustomLabelArray } from 'modules/customLabels/hooks';
import { useFeatureFlag } from 'modules/common/hooks';
import { CustomLabel } from '../labelSchema';

type useBulkCustomSuggestionsActionsProps = {
  fieldName: string;
};
type useBulkCustomSuggestionsActionsResult = {
  enableCustomActions: boolean;
  denyAllCustomLabelSuggestionHandler: () => void;
  acceptAllCustomLabelSuggestionHandler: () => void;
};
export const useBulkCustomSuggestionsActions = ({
  fieldName
}: useBulkCustomSuggestionsActionsProps): useBulkCustomSuggestionsActionsResult => {
  const { control } = useFormContext();
  const { replace } = useCustomLabelArray({ fieldName });
  const isLabelLevelAcceptedByEnabled = useFeatureFlag(
    'isLabelLevelAcceptedByEnabled',
    false
  );
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );

  const fields = useWatch({
    name: `${fieldName}`,
    control
  });

  const enableCustomActions = useMemo(
    () =>
      (fields ?? []).some(
        (field: CustomLabel) =>
          field?.valueSource === LabelValueSource.Suggestion &&
          (isLabelLevelAcceptedByEnabled ? !field?.acceptedBy : true)
      ),
    [fields, isLabelLevelAcceptedByEnabled]
  );

  const denyAllCustomLabelSuggestionHandler = useCallback(() => {
    const userFields = (fields ?? []).filter((field: CustomLabel) => {
      const value = isTimeSignalFederatedSuggestionsEnabled
        ? field?.suggestedValues?.[0]
        : field?.value;
      if (
        (field?.valueSource === LabelValueSource.User ||
          (isLabelLevelAcceptedByEnabled ? field?.acceptedBy : false)) &&
        value
      ) {
        return field;
      }
    });
    replace?.(userFields);
  }, [
    fields,
    isLabelLevelAcceptedByEnabled,
    isTimeSignalFederatedSuggestionsEnabled,
    replace
  ]);

  const acceptAllCustomLabelSuggestionHandler = useCallback(() => {
    const userField = (fields ?? []).map((field: CustomLabel) => {
      const value = isTimeSignalFederatedSuggestionsEnabled
        ? field?.suggestedValues?.[0]
        : field?.value;
      if (
        field.valueSource === LabelValueSource.Suggestion &&
        value &&
        (isLabelLevelAcceptedByEnabled ? !field?.acceptedBy : true)
      ) {
        return {
          ...field,
          valueSource: LabelValueSource.User,
          ...(isLabelLevelAcceptedByEnabled
            ? {
                acceptedBy: field.acceptedBy ?? LabelValueAcceptedBy.User,
                valueSource: field.valueSource ?? LabelValueSource.User
              }
            : {}),
          ...(isTimeSignalFederatedSuggestionsEnabled
            ? { value: field?.suggestedValues?.[0] }
            : {})
        };
      } else {
        return field;
      }
    });
    replace?.(userField);
  }, [
    fields,
    replace,
    isLabelLevelAcceptedByEnabled,
    isTimeSignalFederatedSuggestionsEnabled
  ]);

  return {
    denyAllCustomLabelSuggestionHandler,
    enableCustomActions,
    acceptAllCustomLabelSuggestionHandler
  };
};
